import { useLocation, useSearchParams } from "react-router-dom";
import React, { useState, useEffect, useRef, useCallback } from "react";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import {
  Combine,
  LineChart as LineChartIcon,
  Users,
  Car,
  Activity,
  Map,
  KeyRound,
  Truck,
} from "lucide-react";
import SendCommandModal from "./SendCommandModal";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/authSlice";
import { downloadEntityTemplate, uploadEntityFile } from "../services/importExportService";

// --- Custom Hooks and Components Imports ---
import useAdminData from "./admin/useAdminData";
import useAdminHandlers from "./admin/useAdminHandlers";
import StaticsDashboard from "./admin/StaticsDashboard";
import UserManagement, { EditUserModal, CreateUserHubModal } from "./admin/UserManagement";
import AssetManagement from "./admin/AssetManagement";
import EventLogs from "./admin/EventLogs";
import MapTracker from "./admin/MapTracker";
import ResetPasswordModal from "./admin/ResetPasswordModal";
import AddDeviceModal from "./admin/AddDeviceModal";
import AddAssetModal from "./admin/AddAssetModal";
import EditAssetModal from "./admin/EditAssetModal";
import GeofenceModal from "./admin/GeofenceModal";
import "./admin/admindashboard.css";
import "./admin/dashboard-redesign.css";
import { useGetAlarmsQuery } from "../redux/admin/alarmApi";
import RegisterDeviceModal from "./admin/RegisterDeviceModal";
import DeviceListPortalModal from "./modals/DeviceListPortalModal";

// 🚀 RBAC IMPORTS
import {
  hasPermission,
  hasUserPermission,
  isExplicitSuperAdminRole,
  ROLES,
  getManageableRoles,
  getCreatableRoles,
} from "../utils/rbacHelper";
import QuickActions from "./admin/QuickActions";
import TaskManagement from "./employee/TaskManagement";
import MyTasks from "./employee/MyTasks";
import { useStatus } from "../popup/NotificationContext";
import CreateCustomerModal from "./modals/CreateCustomerModal";
import CreateDealerModal from "./modals/CreateDealerModal";
import SensorAssignModal from "./modals/SensorAssignModal";
import AlarmDashboard from "../popup/AlarmDashboard";
import AlarmPopupSystem from "../popup/AlarmPopupSystem";
import CameraControlPortal from "./cameraV2/CameraControlPortal";
import HistoryPlaybackPage from "../pages/history-playback/HistoryPlaybackPage";
import GlobalDashboardSidebar from "./admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "./admin/useGlobalSidebar";
import WhiteLabelRoutePage from "../modules/whitelabel/Dashboard/WhiteLabelRoutePage";
import {
  buildAdminDashboardPath,
  consumeDashboardNavPayload,
  isDashboardShellPath,
  normalizeDashboardTab,
  readDashboardTabFromSearch,
  navigateToAdminDashboard,
  resolveDashboardTabFromPath,
} from "../router/dashboardNavigation";
import { USER_ROLE_ROUTE_CONFIG } from "./users/userRoleConfig";
import { isDemoReadOnlyUser } from "../utils/demoUser";
import Loader from "../mainframes/Loader";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (r === "superadmin" || r === "super_admin") return ROLES.SUPER_ADMIN;
  if (r === "groupadmin" || r === "dealer") return ROLES.DEALER;
  if (r === "subadmin") return ROLES.SUB_ADMIN;
  if (r === "officeemployee") return ROLES.OFFICE_EMPLOYEE;
  if (r === "fieldemployee") return ROLES.FIELD_EMPLOYEE;
  return r;
};

const toIdString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const isHistoryTab = (tab) => ["history_playback", "history_logs", "history_events", "history_parking"].includes(String(tab || "").trim().toLowerCase());

function AdminDashboard(props) {
  const location = useLocation();

  if (!isDashboardShellPath(location.pathname)) {
    return null;
  }

  return <AdminDashboardShell {...props} />;
}

function AdminDashboardShell({
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { showStatus } = useStatus();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDashboardRoute = isDashboardShellPath(location.pathname);
  // --- 1. Refs & Identifiers ---
  const audioRef = useRef(null);
  const prevEventsRef = useRef();
  const didFetchRef = useRef(false);

  const currentUserRole = normalizeRole(currentUser?.role);
  const currentUserId = currentUser?._id;
  const isViewer = currentUserRole === ROLES.VIEWER;
  const isDemoReadOnly = isDemoReadOnlyUser(currentUser);
  const canAccessWhiteLabel =
    isExplicitSuperAdminRole(currentUser?.role) && !isDemoReadOnly;
  const globalSidebarItems = useGlobalSidebarItems();

  // --- 2. Local States ---
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [deviceAddresses, setDeviceAddresses] = useState({});
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [pendingQuickAction, setPendingQuickAction] = useState(null);
  const [pendingFocusRoute, setPendingFocusRoute] = useState(null);
  const [pendingPlaybackFocus, setPendingPlaybackFocus] = useState(null);
  const [pendingAlarmFocus, setPendingAlarmFocus] = useState(null);
  const [pendingMapStatusFilter, setPendingMapStatusFilter] = useState("all");

  const [internalActiveTab, setInternalActiveTab] = useState(() => {
    try {
      if (propActiveTab) return normalizeDashboardTab(propActiveTab, "assignments");
      const routeTab = resolveDashboardTabFromPath(window.location.pathname, readDashboardTabFromSearch(window.location.search, ""));
      if (routeTab) return routeTab;
      const savedTab = sessionStorage.getItem("adminDashboardActiveTab");
      if (savedTab === "assignDevice" || savedTab === "assignUserToGA")
        return "assignments";
      if (currentUserRole === ROLES.VIEWER) return "map";
      if (currentUserRole === ROLES.FIELD_EMPLOYEE) return "my_tasks";
      return savedTab || "assignments";
    } catch (err) {
      return "assignments";
    }
  });

// Use prop activeTab if provided, otherwise use internal state.
// Outside dashboard routes we should not keep rendering the legacy dashboard shell.
const routeActiveTab = isDashboardRoute
  ? resolveDashboardTabFromPath(location.pathname, readDashboardTabFromSearch(location.search, internalActiveTab))
  : null;
const activeTab = propActiveTab || routeActiveTab || (isDashboardRoute ? internalActiveTab : null);
const setActiveTab = useCallback((nextTab) => {
  const normalized = normalizeDashboardTab(nextTab, activeTab || "assignments");
  if (propSetActiveTab) {
    propSetActiveTab(normalized);
    return;
  }
  setInternalActiveTab(normalized);
  navigate(buildAdminDashboardPath({ tab: normalized }), { replace: true });
}, [activeTab, navigate, propSetActiveTab]);

useEffect(() => {
  if (propActiveTab || !isDashboardRoute) return;
  const routeTab = readDashboardTabFromSearch(location.search, internalActiveTab);
  if (routeTab !== internalActiveTab) {
    setInternalActiveTab(routeTab);
  }
}, [internalActiveTab, isDashboardRoute, location.search, propActiveTab]);

useEffect(() => {
  if (propActiveTab || !isDashboardRoute) return;
  if (location.pathname !== "/dashboard") return;
  if (searchParams.get("tab")) return;
  setSearchParams((current) => {
    const next = new URLSearchParams(current);
    next.set("tab", normalizeDashboardTab(activeTab, "assignments"));
    return next;
  }, { replace: true });
}, [activeTab, isDashboardRoute, location.pathname, propActiveTab, searchParams, setSearchParams]);


  // Update sessionStorage when activeTab changes
  useEffect(() => {
    if (!activeTab) return;
    try {
      sessionStorage.setItem("adminDashboardActiveTab", activeTab);
    } catch (e) {
      console.error("Failed to save tab to sessionStorage:", e);
    }
  }, [activeTab]);

  const [routeStart, setRouteStart] = useState(null);
  const [routeEnd, setRouteEnd] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [showLocationError, setShowLocationError] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [showCreateDealerModal, setShowCreateDealerModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetUserId, setResetUserId] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [newAssetData, setNewAssetData] = useState({
    IMEI: "",
    serialNumber: "",
    model: "",
    type: "hardwire",
    status: "unassigned",
    simCard: { IMSI: "", MSDN: "", provider: "", expiryDate: "" },
    purchaseDate: "",
    notes: "",
  });

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [selectedDeviceForGeofence, setSelectedDeviceForGeofence] =
    useState(null);
  const [unsavedGeofence, setUnsavedGeofence] = useState(null);
  const [geofenceName, setGeofenceName] = useState("");
  const [editingDevice, setEditingDevice] = useState({ assignedToUser: null });
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [assignUserSearchQuery, setAssignUserSearchQuery] = useState("");
  const [groupAdminSearchQuery, setGroupAdminSearchQuery] = useState("");
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [assignToUserId, setAssignToUserId] = useState("");
  const [showRegisterDeviceModal, setShowRegisterDeviceModal] = useState(false);

  const [assignUserToGroupAdminUserId, setAssignUserToGroupAdminUserId] =
    useState("");
  const [assignUserToGroupAdminTargetId, setAssignUserToGroupAdminTargetId] =
    useState("");

  // State for Sensor Modal
  const [showSensorModal, setShowSensorModal] = useState(false);
  const [selectedDeviceForSensor, setSelectedDeviceForSensor] = useState(null);

  // Handler
  const handleSensorManagement = useCallback((device) => {
    setSelectedDeviceForSensor(device);
    setShowSensorModal(true);
  }, []);

  // --- 3. Authorization Logic ---
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);
  const can = useCallback(
    (action) => hasUserPermission(currentUser, action),
    [currentUser],
  );
  const setMessageWithTimeout = useCallback((setter, msg) => {
    setter(msg);
    setTimeout(() => setter(""), 3000);
  }, []);

  const rolesToFetch = [
    "admin",
    "manager",
    "hr",
    "field_emp",
    "office_emp",
    "sub_admin",
    "group_admin",
    "client",
    "user",
    "viewer",
  ];
  const skipAdminDataFetch = currentUserRole
    ? !rolesToFetch.includes(currentUserRole)
    : true;

  // --- 4. Custom Hooks ---
  const {
    loading,
    error,
    users,
    devices,
    devicesOnMap,
    deviceCounts,
    totalDeviceCount,
    progressiveLoading,
    deviceEvents,
    eventLoading,
    eventError,
    assetsData,
    isAssetsLoading,
    assetError,
    editingUser,
    setEditingUser,
    fetchAdminData,
    fetchAllAssets,
    updateMapViewport,
  } = useAdminData({
    currentUser,
    handleLogout,
    setMessageWithTimeout,
    setErrorMessage,
    prevEventsRef,
    didFetchRef,
    setDeviceAddresses,
    setSelectedDevices,
    activeTab,
    selectedDevices,
    audioRef,
    skipFetch: skipAdminDataFetch,
  });
useEffect(() => {
  if (!isDashboardRoute || !activeTab) return;
  const statusFromQuery = searchParams.get("status");
  if (statusFromQuery) {
    setPendingMapStatusFilter(String(statusFromQuery));
  }

  const navToken = searchParams.get("nav");
  const navPayload = consumeDashboardNavPayload(navToken);
  const fallbackState = location.state && typeof location.state === "object" ? location.state : null;
  const mergedPayload = {
    ...(navPayload || {}),
    ...(fallbackState || {}),
  };
  const targetMapTab = activeTab === "map" ? null : "map";

  if (mergedPayload.quickAction) {
    setPendingQuickAction(mergedPayload.quickAction);
  }
  if (mergedPayload.focusRoute) {
    setPendingFocusRoute(mergedPayload.focusRoute);
  }
  if (mergedPayload.playbackFocus) {
    setPendingPlaybackFocus(mergedPayload.playbackFocus);
  }
  if (mergedPayload.alarmFocus) {
    setPendingAlarmFocus(mergedPayload.alarmFocus);
  }

  if (targetMapTab && (mergedPayload.quickAction || mergedPayload.focusRoute || mergedPayload.playbackFocus || mergedPayload.alarmFocus)) {
    setActiveTab(targetMapTab);
    return;
  }

  if (!navToken && !fallbackState) return;

  navigate(
    buildAdminDashboardPath({
      tab: activeTab,
      mapStatusFilter: statusFromQuery,
    }),
    { replace: true, state: null }
  );
}, [activeTab, isDashboardRoute, location.state, navigate, searchParams, setActiveTab]);
  const handlers = useAdminHandlers({
    currentUser,
    backendUrl,
    handleLogout,
    setMessageWithTimeout,
    setSuccessMessage,
    setErrorMessage,
    fetchAdminData,
    fetchAllAssets,
    resetUserId,
    newPassword,
    confirmPassword,
    setNewPassword,
    setConfirmPassword,
    setShowResetPasswordModal,
    editingAsset,
    setEditingAsset,
    setShowEditAssetModal,
    newAssetData,
    setNewAssetData,
    setShowAddAssetModal,
    assignDeviceId,
    assignToUserId,
    setAssignDeviceId,
    setAssignToUserId,
    assignUserToGroupAdminUserId,
    assignUserToGroupAdminTargetId,
    setAssignUserToGroupAdminUserId,
    setAssignUserToGroupAdminTargetId,
    editingUser,
    setEditingUser,
    editingDevice,
    setEditingDevice,
    setShowEditDeviceModal,
    selectedDeviceForGeofence,
    setSelectedDeviceForGeofence,
    unsavedGeofence,
    geofenceName,
    setShowGeofenceModal,
    setUnsavedGeofence,
    setGeofenceName,
    setRouteStart,
    setRouteEnd,
    setRouteDetails,
    setLocationError,
    setShowLocationError,
    devices,
  });

  const {
    handleOpenCommandModal,
    handleCloseCommandModal,
    handleIMEIValidation,
    handleCancelValidation,
    handleResetPassword,
    handleAssignDevice,
    handleAssignUserToGroupAdmin,
    handleRemoveManagedUser,
    handleEditUser,
    handleDeleteUser,
    handleRestoreUser,
    handleDeleteDevice,
    handleAddDevice,
    handleAddAsset,
    handleEditAsset,
    handleDeleteAsset,
    handlePermanentDeleteAsset,
    handleRestoreAsset,
    handleDownloadTemplate,
    handleBulkFileChange,
    handleBulkUpload,
    handleExportToExcel,
    handleGeofenceManagement,
    onGeofenceCreated,
    onGeofenceEdited,
    onGeofenceDeleted,
    handleSaveGeofence,
    getDirections,
    formatTimestamp12h,
    handleCreateUser,
    getVehicleIcon,
    vehicleTypeIcons,
    newDeviceModel,
    setNewDeviceModel,
    newDeviceName,
    setNewDeviceName,
    newDeviceIMEI,
    setNewDeviceIMEI,
    newDeviceMSDN,
    setNewDeviceMSDN,
    newDeviceIMSI,
    setNewDeviceIMSI,
    newDeviceType,
    setNewDeviceType,
    newUsername,
    setNewUsername,
    newEmail,
    setNewEmail,
    newMobile,
    setNewMobile,
    newPassword: newModalPassword,
    setNewPassword: setNewModalPassword,
    newUserRole,
    setNewUserRole,
    bulkFile,
    uploading,
    showCommandModal: commandModalVisible,
    deviceForCommand: commandDevice,
    commandModalOptions,
    isIMEIValid: isIMEIValidHandler,
    handleLoginAsUser,
  } = handlers;

  useEffect(() => {
    if (isViewer && activeTab !== "map") {
      setActiveTab("map");
    }
  }, [isViewer, activeTab, setActiveTab]);

  useEffect(() => {
    if (!activeTab) return;
    try {
      sessionStorage.setItem("adminDashboardActiveTab", activeTab);
    } catch (e) {
      console.error("Failed to save:", e);
    }
  }, [activeTab]);

  // --- 5. Effects ---
  useEffect(() => {
    if (
      error &&
      !error.toString().includes("authorized") &&
      error.toString().trim().toLowerCase() !== "system sync failed"
    )
      showStatus("error", `Sync failed: ${error}`);
  }, [error, showStatus]);

  useEffect(() => {
    if (!showGeofenceModal || !selectedDeviceForGeofence) return;
    const deviceGeofence = selectedDeviceForGeofence.geofences?.[0];
    if (!deviceGeofence) {
      setUnsavedGeofence(null);
      setGeofenceName("");
      return;
    }
    if (deviceGeofence.type === "circle") {
      setUnsavedGeofence({
        type: "circle",
        data: {
          center: {
            lat: Number(deviceGeofence.center.lat),
            lng: Number(deviceGeofence.center.lng),
          },
          radius: Number(deviceGeofence.radius),
        },
      });
      setGeofenceName(deviceGeofence.name);
    }
    if (deviceGeofence.type === "polygon") {
      const coords = deviceGeofence.polygon?.coordinates?.[0]?.map(
        ([lng, lat]) => ({ lat, lng }),
      );
      if (coords?.length) {
        setUnsavedGeofence({ type: "polygon", data: coords });
        setGeofenceName(deviceGeofence.name);
      }
    }
  }, [
    showGeofenceModal,
    selectedDeviceForGeofence,
    setUnsavedGeofence,
    setGeofenceName,
  ]);

  // --- 6. Early Returns ---
  if (!isDashboardRoute) return null;
  if (!currentUser)
    return <Loader fullscreen label="Authenticating..." background="#f8fafc" />;
  if (loading && !skipAdminDataFetch)
    return <Loader fullscreen label="Loading Dashboard..." background="#f8fafc" />;

  const manageableRoles = isDemoReadOnly ? [] : getManageableRoles(currentUserRole);
  const creatableRoles = isDemoReadOnly ? [] : getCreatableRoles(currentUserRole);

  // ✅ CRITICAL FIX: Ensure arrays are always defined with fallback
  const safeUsers = Array.isArray(users) ? users : [];
  const safeDevices = Array.isArray(devices) ? devices : [];

  const scopedUsers = safeUsers.filter((user) => {
    const role = normalizeRole(user.role);
    const userId = toIdString(user?._id);
    const createdBy = toIdString(user?.createdBy);
    const dealerOwner = toIdString(user?.dealerOwner);
    const managedUserIds = (currentUser?.managedUsers || []).map((managedUser) =>
      toIdString(managedUser?._id || managedUser)
    );

    if (currentUserRole === ROLES.SUPER_ADMIN) return role !== ROLES.SUPER_ADMIN;
    if ([ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE, ROLES.HR].includes(currentUserRole)) {
      return manageableRoles.includes(role);
    }

    return (
      userId === currentUserId ||
      manageableRoles.includes(role) &&
        (createdBy === currentUserId ||
          dealerOwner === currentUserId ||
          managedUserIds.includes(userId))
    );
  });

  const assignableUsersForDevices = scopedUsers.filter((user) =>
    manageableRoles.includes(normalizeRole(user.role))
  );

  // ✅ ROLE-BASED USER FILTERING
  const assignableUsersForGroupAdmin = safeUsers.filter((user) => {
    if (
      currentUserRole === ROLES.SUPER_ADMIN ||
      currentUserRole === ROLES.MANAGER
    ) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    if (currentUserRole === ROLES.SUB_ADMIN) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    if (currentUserRole === ROLES.DEALER) {
      return (
        normalizeRole(user.role) === ROLES.USER ||
        normalizeRole(user.role) === ROLES.CLIENT ||
        normalizeRole(user.role) === ROLES.VIEWER
      );
    }
    return false;
  });

  // ✅ ROLE-BASED ADMIN FILTERING
  const groupAdmins = safeUsers.filter((user) => {
    if (
      currentUserRole === ROLES.SUPER_ADMIN ||
      currentUserRole === ROLES.MANAGER
    ) {
      return normalizeRole(user.role) === ROLES.SUB_ADMIN || normalizeRole(user.role) === ROLES.DEALER;
    }
    if (currentUserRole === ROLES.OFFICE_EMPLOYEE) {
      return normalizeRole(user.role) === ROLES.SUB_ADMIN;
    }
    if (currentUserRole === ROLES.SUB_ADMIN) {
      return normalizeRole(user.role) === ROLES.DEALER;
    }
    return false;
  });

  // const { data: alarmsData = [], isLoading: alarmsLoading } = useGetAlarmsQuery({
  //   status: 'active',
  //   limit: 50
  // });

  const assignmentLabels = {
    userLabel: "Select User",
    adminLabel:
      currentUserRole === ROLES.OFFICE_EMPLOYEE
        ? "Select Sub-Admin"
        : currentUserRole === ROLES.SUB_ADMIN
          ? "Select Dealer"
          : "Select Admin/Dealer",
    adminPlaceholder:
      currentUserRole === ROLES.OFFICE_EMPLOYEE
        ? "Sub-Admin Name"
        : currentUserRole === ROLES.SUB_ADMIN
          ? "Dealer Name"
          : "Sub-Admin or Dealer Name",
  };

  const showAdminSidebar = !isViewer;
  const useImmersiveShell = false;
  const showGlobalTrackmasterSidebar = showAdminSidebar;
  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const globalSidebarItemsLegacy = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <LineChartIcon size={18} />,
      active: location.pathname === "/dashboard",
      onClick: () => navigate("/dashboard"),
    },
    {
      key: "vehicles",
      label: "Vehicles",
      icon: <Car size={18} />,
      active: location.pathname === "/dashboard" && activeTab === "devices",
      onClick: () => setActiveTab("devices"),
    },
    {
      key: "map",
      label: "Map",
      icon: <Map size={18} />,
      active: location.pathname === "/dashboard" && activeTab === "map",
      defaultExpanded: location.pathname === "/dashboard" && activeTab === "map",
      children: [
        {
          key: "map-live-tracking",
          label: "Live Tracking",
          active: location.pathname === "/dashboard" && activeTab === "map",
          onClick: () => setActiveTab("map"),
        },
      ],
    },
    {
      key: "history",
      label: "History",
      icon: <HistoryOutlinedIcon fontSize="small" />,
      active: location.pathname === "/dashboard" && isHistoryTab(activeTab),
      defaultExpanded: location.pathname === "/dashboard" && isHistoryTab(activeTab),
      children: [
        { key: "history-playback", label: "Playback", active: activeTab === "history_playback", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_playback" }) },
        { key: "history-logs", label: "Logs", active: activeTab === "history_logs", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_logs" }) },
        { key: "history-events", label: "Events", active: activeTab === "history_events", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_events" }) },
        { key: "history-parking", label: "Parking", active: activeTab === "history_parking", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_parking" }) },
      ],
    },
    {
      key: "trips",
      label: "Trips",
      icon: <Activity size={18} />,
      active: location.pathname === "/details",
      onClick: () => navigate("/logistic/trips"),
    },
    {
      key: "geofences",
      label: "Geofences",
      icon: <Map size={18} />,
      active: location.pathname === "/geofences",
      onClick: () => navigate("/geofences"),
    },
    {
      key: "notifications",
      label: "Notifications",
      icon: <Activity size={18} />,
      active: location.pathname === "/notifications",
      onClick: () => navigate("/notifications"),
    },
    {
      key: "reports",
      label: "Reports",
      icon: <Combine size={18} />,
      active: location.pathname === "/reports",
      onClick: () => navigate("/reports"),
    },
    {
      key: "logistic",
      label: "Logistic",
      icon: <Truck size={18} />,
      active: location.pathname.startsWith("/logistic"),
      children: [
        { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
        { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
        { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
        { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
      ],
    },
    {
      key: "organization",
      label: "Groups",
      icon: <Users size={18} />,
      active: location.pathname.startsWith("/groups"),
      onClick: () => navigate("/groups"),
    },
    {
      key: "users",
      label: "Users & Roles",
      icon: <Users size={18} />,
      active: location.pathname.startsWith("/users") || (location.pathname === "/dashboard" && activeTab === "users"),
      children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === item.path,
        onClick: () => navigate(item.path),
      })),
    },
    {
      key: "billing",
      label: "Billing & Licenses",
      icon: <KeyRound size={18} />,
      active: location.pathname === "/licenses",
      children: [
        { key: "licenses", label: "Licenses", active: location.pathname === "/licenses" && !location.search.includes("section="), onClick: () => navigate("/licenses") },
        { key: "all-licenses", label: "All Licenses", active: location.pathname === "/licenses" && location.search.includes("section=all-licenses"), onClick: () => navigate("/licenses?section=all-licenses") },
        { key: "purchase-license", label: "Purchase License", active: location.pathname === "/licenses" && location.search.includes("section=purchase-license"), onClick: () => navigate("/licenses?section=purchase-license") },
        { key: "license-summary", label: "License Summary", active: location.pathname === "/licenses" && location.search.includes("section=license-summary"), onClick: () => navigate("/licenses?section=license-summary") },
        { key: "transactions", label: "Transactions", active: location.pathname === "/licenses" && location.search.includes("section=transactions"), onClick: () => navigate("/licenses?section=transactions") },
      ],
    },
    {
      key: "settings",
      label: "Settings",
      icon: <Combine size={18} />,
      active: location.pathname === "/profile",
      onClick: () => navigate("/profile"),
    },
  ];

  void globalSidebarItemsLegacy;

  return (
    <div
      className={`dashboard-page-shell admin-dashboard-shell ${showGlobalTrackmasterSidebar ? "has-global-sidebar" : "is-bare-shell"} ${isViewer ? "viewer-mode" : ""} ${useImmersiveShell ? "no-sidebar" : "standard-shell"} ${activeTab === "map" ? "map-tab-active" : ""}`}
    >
      <AlarmPopupSystem />
      <audio
        ref={audioRef}
        src="/assets/event.wav"
        muted
        autoPlay
        preload="auto"
      ></audio>

      {showGlobalTrackmasterSidebar ? (
        <GlobalDashboardSidebar
          items={globalSidebarItems}
          profileName={profileName}
          roleLabel={roleLabel}
          onSupport={isDemoReadOnly ? undefined : () => navigate("/profile?section=support")}
          onLogout={handleLogout}
        />
      ) : null}

      <main className="dashboard-content admin-dashboard-shell__content">
        <div className={`ad-main-content admin-dashboard-shell__main ${useImmersiveShell ? "full-width immersive-shell" : "standard-shell"}`}>
          <div className={`ad-tab-container admin-dashboard-shell__tab-container ${useImmersiveShell ? "immersive-shell" : "standard-shell"}`}>
          {activeTab === "statics" && can("viewDashboard") && (
            <StaticsDashboard deviceCounts={deviceCounts} />
          )}

          {activeTab === "map" && (
           <MapTracker
  devicesOnMap={devicesOnMap.length ? devicesOnMap : safeDevices}
  sidebarDevices={safeDevices}
              deviceEvents={deviceEvents}
              selectedDevices={selectedDevices}
              setSelectedDevices={setSelectedDevices}
              deviceAddresses={deviceAddresses}
              getDirections={getDirections}
              formatTimestamp12h={formatTimestamp12h}
              handleOpenCommandModal={
                can("sendCommands") ? handleOpenCommandModal : null
              }
              routeStart={routeStart}
              routeEnd={routeEnd}
              routeDetails={routeDetails}
              setRouteDetails={setRouteDetails}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              locationError={locationError}
              showLocationError={showLocationError}
              setShowLocationError={setShowLocationError}
              deviceCounts={deviceCounts}
              totalDeviceCount={totalDeviceCount}
              vehicleTypeIcons={vehicleTypeIcons}
              getVehicleIcon={getVehicleIcon}
              viewerMode={isViewer}
              currentUserRole={currentUserRole}
              quickAction={pendingQuickAction}
              clearQuickAction={() => setPendingQuickAction(null)}
              externalRouteFocus={pendingFocusRoute}
              clearExternalRouteFocus={() => setPendingFocusRoute(null)}
              externalPlaybackFocus={pendingPlaybackFocus}
              clearExternalPlaybackFocus={() => setPendingPlaybackFocus(null)}
              externalAlarmFocus={pendingAlarmFocus}
              clearExternalAlarmFocus={() => setPendingAlarmFocus(null)}
              initialSidebarStatus={pendingMapStatusFilter}
              progressiveLoading={progressiveLoading}
              onMapViewportChange={updateMapViewport}
            />
          )}

          {activeTab === "white_label" && canAccessWhiteLabel && (
            <WhiteLabelRoutePage />
          )}

          {activeTab === "users" && can("viewUsers") && (
            <UserManagement
              users={scopedUsers}
              creatableRoles={creatableRoles}
              manageableRoles={manageableRoles}
              currentUser={currentUser}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              setEditingUser={setEditingUser}
              handleDeleteUser={handleDeleteUser}
              handleRestoreUser={handleRestoreUser}
              handleRemoveManagedUser={handleRemoveManagedUser}
              setResetUserId={setResetUserId}
              setShowResetPasswordModal={setShowResetPasswordModal}
              handleLoginAsUser={handleLoginAsUser}
              setShowCreateUserModal={setShowCreateUserModal}
              canEdit={can("editUser")}
              canDelete={can("deleteUser")}
              canCreate={can("createUser")}
              canAssignDevice={can("createDevice")}
              canAssignUserToGA={can("assignUserToGA")}
              assignmentLabels={assignmentLabels}
              handleAssignDevice={handleAssignDevice}
              handleAssignUserToGroupAdmin={handleAssignUserToGroupAdmin}
              safeDevices={safeDevices}
              assignableUsersForDevices={assignableUsersForDevices}
              assignableUsersForGroupAdmin={assignableUsersForGroupAdmin}
              groupAdmins={groupAdmins}
              deviceSearchQuery={deviceSearchQuery}
              setDeviceSearchQuery={setDeviceSearchQuery}
              assignDeviceId={assignDeviceId}
              setAssignDeviceId={setAssignDeviceId}
              userSearchQuery={userSearchQuery}
              setUserSearchQuery={setUserSearchQuery}
              assignToUserId={assignToUserId}
              setAssignToUserId={setAssignToUserId}
              assignUserSearchQuery={assignUserSearchQuery}
              setAssignUserSearchQuery={setAssignUserSearchQuery}
              assignUserToGroupAdminUserId={assignUserToGroupAdminUserId}
              setAssignUserToGroupAdminUserId={setAssignUserToGroupAdminUserId}
              groupAdminSearchQuery={groupAdminSearchQuery}
              setGroupAdminSearchQuery={setGroupAdminSearchQuery}
              assignUserToGroupAdminTargetId={assignUserToGroupAdminTargetId}
              setAssignUserToGroupAdminTargetId={setAssignUserToGroupAdminTargetId}
              onRefreshData={fetchAdminData}
            />
          )}

          {activeTab === "devices" && can("viewDevices") && (
            <DeviceListPortalModal asPage />
          )}
          {!isDemoReadOnly ? (
            <RegisterDeviceModal
              open={showRegisterDeviceModal}
              onClose={() => setShowRegisterDeviceModal(false)}
              onCreated={() => fetchAdminData()}
            />
          ) : null}

          {activeTab === "assignments" && can("viewDashboard") && (
            <div className="ad-grid-layout">
              <div className="ad-grid-right">
                <QuickActions
                  currentUser={currentUser}
                  currentUserRole={currentUserRole}
                  setShowAddDeviceModal={setShowAddDeviceModal}
                  setShowCreateUserModal={setShowCreateUserModal}
                  setShowAddAssetModal={setShowAddAssetModal}
                  setShowCreateCustomerModal={setShowCreateCustomerModal}
                  setShowCreateDealerModal={setShowCreateDealerModal}
                />
              </div>
            </div>
          )}

          {activeTab === "alarms" && can("viewEvents") && (
            <AlarmDashboard
              currentUser={currentUser}
              currentUserRole={currentUserRole}
              initialAlarms={[]}
            />
          )}

          {activeTab === "events" && can("viewEvents") && (
            <EventLogs
              deviceEvents={deviceEvents}
              eventLoading={eventLoading}
              eventError={eventError}
              formatTimestamp12h={formatTimestamp12h}
            />
          )}

          {isHistoryTab(activeTab) && (
            <HistoryPlaybackPage
              devices={safeDevices}
              getVehicleIcon={getVehicleIcon}
              initialTab={String(activeTab || "history_playback").replace("history_", "") || "playback"}
            />
          )}

          {activeTab === "live_camera" && (
            <CameraControlPortal />
          )}

          {activeTab === "task_management" &&
            ["admin", "manager", "office_emp"].includes(currentUserRole) && (
              <TaskManagement />
            )}
          {activeTab === "my_tasks" && currentUserRole === "field_emp" && (
            <MyTasks />
          )}

          {activeTab === "assignments" && can("viewAssets") && (
            <div className="ad-full-width">
              <AssetManagement
                assetsData={assetsData}
                isAssetsLoading={isAssetsLoading}
                assetError={assetError}
                handleDownloadTemplate={handleDownloadTemplate}
                handleBulkFileChange={handleBulkFileChange}
                handleBulkUpload={handleBulkUpload}
                handleExportToExcel={handleExportToExcel}
                setEditingAsset={setEditingAsset}
                setShowEditAssetModal={setShowEditAssetModal}
                handleRestoreAsset={handleRestoreAsset}
                handlePermanentDeleteAsset={handlePermanentDeleteAsset}
                handleDeleteAsset={handleDeleteAsset}
                bulkFile={bulkFile}
                uploading={uploading}
                currentUserRole={currentUserRole}
                setMessageWithTimeout={setMessageWithTimeout}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
              />
            </div>
          )}

          {/* --- MODALS --- */}
          {!isDemoReadOnly && editingUser && (
            <EditUserModal
              editingUser={editingUser}
              setEditingUser={setEditingUser}
              handleEditUser={handleEditUser}
              currentUser={currentUser}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              creatableRoles={creatableRoles}
            />
          )}
          {!isDemoReadOnly && showAddDeviceModal && (
            <AddDeviceModal
              isIMEIValid={isIMEIValidHandler}
              handleIMEIValidation={handleIMEIValidation}
              handleCancelValidation={handleCancelValidation}
              handleAddDevice={handleAddDevice}
              newDeviceModel={newDeviceModel}
              setNewDeviceModel={setNewDeviceModel}
              newDeviceName={newDeviceName}
              setNewDeviceName={setNewDeviceName}
              newDeviceIMEI={newDeviceIMEI}
              setNewDeviceIMEI={setNewDeviceIMEI}
              newDeviceMSDN={newDeviceMSDN}
              setNewDeviceMSDN={setNewDeviceMSDN}
              newDeviceIMSI={newDeviceIMSI}
              setNewDeviceIMSI={setNewDeviceIMSI}
              newDeviceType={newDeviceType}
              setNewDeviceType={setNewDeviceType}
              setShowAddDeviceModal={setShowAddDeviceModal}
              newDeviceVehicleType={handlers.newDeviceVehicleType}
              setNewDeviceVehicleType={handlers.setNewDeviceVehicleType}
            />
          )}
          {showEditAssetModal && (
            <EditAssetModal
              editingAsset={editingAsset}
              setEditingAsset={setEditingAsset}
              handleEditAsset={handleEditAsset}
              setShowEditAssetModal={setShowEditAssetModal}
            />
          )}
          {!isDemoReadOnly && showAddAssetModal && (
            <AddAssetModal
              newAssetData={newAssetData}
              setNewAssetData={setNewAssetData}
              handleAddAsset={handleAddAsset}
              setShowAddAssetModal={setShowAddAssetModal}
            />
          )}
          {!isDemoReadOnly && showCreateUserModal && (
            <CreateUserHubModal
              open={showCreateUserModal}
              onClose={() => setShowCreateUserModal(false)}
              users={scopedUsers}
              creatableRoles={creatableRoles}
              currentUser={currentUser}
              currentUserRole={currentUserRole}
              handleDeleteUser={handleDeleteUser}
              handleRestoreUser={handleRestoreUser}
              handleLoginAsUser={handleLoginAsUser}
              onDownloadUsersTemplate={() => downloadEntityTemplate("users")}
              onUsersFileSelected={async (event) => {
                const file = event?.target?.files?.[0];
                if (!file) return;
                await uploadEntityFile("users", file, { validateOnly: false });
                fetchAdminData?.();
              }}
              canLoginAsUser={can("loginAsUser")}
              setResetUserId={setResetUserId}
              setShowResetPasswordModal={setShowResetPasswordModal}
              onRefresh={() => fetchAdminData?.()}
            />
          )}
          {showGeofenceModal && selectedDeviceForGeofence && (
            <GeofenceModal
              selectedDeviceForGeofence={selectedDeviceForGeofence}
              geofenceName={geofenceName}
              setGeofenceName={setGeofenceName}
              unsavedGeofence={unsavedGeofence}
              setUnsavedGeofence={setUnsavedGeofence}
              onGeofenceCreated={onGeofenceCreated}
              onGeofenceEdited={onGeofenceEdited}
              onGeofenceDeleted={onGeofenceDeleted}
              handleSaveGeofence={handleSaveGeofence}
              setShowGeofenceModal={setShowGeofenceModal}
              successMessage={successMessage}
              errorMessage={errorMessage}
              getVehicleIcon={getVehicleIcon}
              handleDeleteGeofence={handlers.handleDeleteGeofence} // ✅ ADD THIS

            />
          )}
          {!isDemoReadOnly && showEditDeviceModal && editingDevice && (
            <RegisterDeviceModal
              open={showEditDeviceModal}
              mode="edit"
              initialDevice={editingDevice}
              onClose={() => {
                setShowEditDeviceModal(false);
                setEditingDevice(null);
              }}
              onUpdated={() => {
                setShowEditDeviceModal(false);
                setEditingDevice(null);
                fetchAdminData?.();
              }}
            />
          )}
          {commandModalVisible && (
            <SendCommandModal
              device={commandDevice}
              onClose={handleCloseCommandModal}
              assetsData={assetsData}
              uiMode={commandModalOptions?.uiMode || "simple"}
              commandScope={commandModalOptions?.commandScope || "full"}
              initialCommandType={commandModalOptions?.initialCommandType || ""}
            />
          )}
          {showResetPasswordModal && (
            <ResetPasswordModal
              handleResetPassword={handleResetPassword}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              setShowResetPasswordModal={setShowResetPasswordModal}
            />
          )}
          {!isDemoReadOnly && showCreateCustomerModal && (
            <CreateCustomerModal
              handleCreateUser={handleCreateUser}
              setShowCreateCustomerModal={setShowCreateCustomerModal}
              newUsername={newUsername}
              setNewUsername={setNewUsername}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newMobile={newMobile}
              setNewMobile={setNewMobile}
              newPassword={newModalPassword}
              setNewPassword={setNewModalPassword}
              currentUserRole={currentUserRole}
            />
          )}
          {!isDemoReadOnly && showCreateDealerModal && (
            <CreateDealerModal
              handleCreateUser={handleCreateUser}
              setShowCreateDealerModal={setShowCreateDealerModal}
              newUsername={newUsername}
              setNewUsername={setNewUsername}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newMobile={newMobile}
              setNewMobile={setNewMobile}
              newPassword={newModalPassword}
              setNewPassword={setNewModalPassword}
              currentUserRole={currentUserRole}
            />
          )}
          {showSensorModal && selectedDeviceForSensor && (
            <SensorAssignModal
              device={selectedDeviceForSensor}
              onClose={() => {
                setShowSensorModal(false);
                setSelectedDeviceForSensor(null);
              }}
            />
          )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
